import React from 'react';
import { UserRoundIcon, CheckIcon, ArrowDownIcon, ClockIcon, HeartIcon, FileTextIcon, MapPinIcon, ShieldCheckIcon, CheckCircle2Icon } from 'lucide-react';
import type { ShiftIconId } from '../../data/horizonShifts';
type Props = {
  isFR: boolean;
};
const floatCard = 'rounded-xl border border-[#EDEEF3] bg-white shadow-[0_6px_20px_rgba(79,70,229,0.08)]';

/* 01 — Request → Action → Result */
function ActionVisual({
  isFR
}: Props) {
  return <div className="relative w-full max-w-[220px] h-[230px] mx-auto" aria-hidden="true" data-id="element-3736" data-mp="iav39g7bsi70">
      {/* Partially visible phone frame */}
      <div className="absolute right-1 top-0 w-[150px] h-[250px] rounded-[26px] border-[5px] border-[#ECEDF2] bg-[#FBFBFD] overflow-hidden" data-id="element-3737" data-mp="s653pzi2shio">
        <div className="flex items-center justify-between px-4 pt-3" data-id="element-3738" data-mp="w7h0cozda30d">
          <span className="block w-4 h-1 rounded-full bg-[#C9CBD6]" data-id="element-3739" data-mp="2tz36an1nmogt" />
          <span className="block w-8 h-1.5 rounded-full bg-[#C9CBD6]" data-id="element-3740" data-mp="2xzd8dombsuqe" />
        </div>
      </div>
      {/* Request */}
      <div className="absolute left-0 top-[52px] w-[170px] rounded-xl border border-[#E1DFF8] bg-[#F4F3FE] px-3 py-2.5 flex items-center gap-2.5 shadow-[0_6px_20px_rgba(79,70,229,0.08)]" data-id="element-3741" data-mp="sac87mdsuznj">
        <UserRoundIcon className="w-4 h-4 shrink-0 text-[#4F46E5]" strokeWidth={1.8} data-id="element-3742" data-mp="ijrby5llm77s" />
        <span className="font-sans text-[11.5px] text-[#4F46E5] leading-snug" data-id="element-3743" data-mp="dlg2vx34o0qm">
          {isFR ? '« Inscris-moi au camp de jour. »' : '“Sign me up for day camp.”'}
        </span>
      </div>
      <ArrowDownIcon className="absolute left-[140px] top-[108px] w-4 h-4 text-[#4F46E5]" strokeWidth={1.8} data-id="element-3744" data-mp="169vfkcyse6ys" />
      {/* Result */}
      <div className={`${floatCard} absolute left-0 top-[132px] w-[182px] px-3 py-3 flex items-center gap-2.5`} data-id="element-3745" data-mp="119za09afy7h4">
        <span className="w-6 h-6 shrink-0 rounded-full bg-[#22A55B] flex items-center justify-center" data-id="element-3746" data-mp="wanut4pq3a1n">
          <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-3747" data-mp="3m1sp8u9u4a5c" />
        </span>
        <span className="min-w-0" data-id="element-3748" data-mp="3h0v9spg7lu7j">
          <span className="block font-sans text-[12px] font-medium text-[#121212] leading-tight" data-id="element-3749" data-mp="3h410280kr24u">
            {isFR ? 'Inscription confirmée' : 'Registration confirmed'}
          </span>
          <span className="block font-sans text-[10.5px] text-[#8A90A0] mt-0.5" data-id="element-3750" data-mp="2d3j8rr2qeqvf">
            {isFR ? 'Camp de jour · Été 2026' : 'Day camp · Summer 2026'}
          </span>
        </span>
      </div>
    </div>;
}

/* 02 — Identity → Context */
function ContextVisual({
  isFR
}: Props) {
  const rows = [{
    icon: ClockIcon,
    label: isFR ? 'Historique' : 'History'
  }, {
    icon: HeartIcon,
    label: isFR ? 'Préférences' : 'Preferences'
  }, {
    icon: FileTextIcon,
    label: isFR ? 'Dossiers actifs' : 'Active files'
  }, {
    icon: MapPinIcon,
    label: isFR ? 'Contexte géographique' : 'Geographic context'
  }];
  return <div className="w-full max-w-[200px] mx-auto space-y-1.5" aria-hidden="true" data-id="element-3751" data-mp="1laskc2rai6gq">
      <div className={`${floatCard} px-3 py-3 flex items-center gap-3`} data-id="element-3752" data-mp="16ge7kjybjtai">
        <span className="w-9 h-9 shrink-0 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-3753" data-mp="1bdnu9rubpqun">
          <UserRoundIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.8} data-id="element-3754" data-mp="rtoybqe9m1qx" />
        </span>
        <span data-id="element-3755" data-mp="nsdgi71mukta">
          <span className="block font-sans text-[12px] font-medium text-[#121212]" data-id="element-3756" data-mp="nswnispqvosj">{isFR ? 'Citoyen' : 'Resident'}</span>
          <span className="block font-sans text-[11px] text-[#5B6478]" data-id="element-3757" data-mp="11p0fefd5bl1u">Brossard, QC</span>
        </span>
      </div>
      {rows.map(({
      icon: Icon,
      label
    }) => <div key={label} className={`${floatCard} px-3 py-2.5 flex items-center gap-3`} data-id="element-3758" data-mp="1aqq7zoirkq17">
          <Icon className="w-4 h-4 shrink-0 text-[#4F46E5]" strokeWidth={1.7} data-id="element-3759" data-mp="16qzfngdla1uk" />
          <span className="font-sans text-[11.5px] text-[#121212]" data-id="element-3760" data-mp="1ki4b069xc7z3">{label}</span>
        </div>)}
    </div>;
}

/* 03 — Verify → Authorize → Act */
function TrustVisual({
  isFR
}: Props) {
  const rows = isFR ? ['Contexte validé', 'Règles appliquées', 'Action autorisée'] : ['Context validated', 'Rules applied', 'Action authorized'];
  return <div className="w-full max-w-[210px] mx-auto" aria-hidden="true" data-id="element-3761" data-mp="1piihhjvvn9v0">
      <div className={`${floatCard} px-3 py-3 flex items-center gap-3`} data-id="element-3762" data-mp="3iymxcoj1tvke">
        <span className="w-9 h-9 shrink-0 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-3763" data-mp="3nxzh30emimir">
          <ShieldCheckIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} data-id="element-3764" data-mp="3soy1ab2qg4ra" />
        </span>
        <span className="flex-1 min-w-0" data-id="element-3765" data-mp="1jg8jkcby1k7">
          <span className="block font-sans text-[12px] font-medium text-[#121212] leading-tight" data-id="element-3766" data-mp="1i519ajoeboc">
            {isFR ? 'Vérification' : 'Permission'}
          </span>
          <span className="block font-sans text-[11px] text-[#5B6478]" data-id="element-3767" data-mp="34yu1yfwkur63">
            {isFR ? 'des autorisations' : 'check'}
          </span>
        </span>
        <span className="w-5 h-5 shrink-0 rounded-full bg-[#22A55B] flex items-center justify-center" data-id="element-3768" data-mp="3a12td7tsxl63">
          <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-3769" data-mp="3evgln1d63c6j" />
        </span>
      </div>
      <div className="relative pl-6 mt-2" data-id="element-3770" data-mp="patw7czjd8yu">
        <span className="absolute left-2 top-0 bottom-[18px] w-px bg-[#4F46E5]/60" data-id="element-3771" data-mp="u6j17vubri2j" />
        <ul className="space-y-2" data-id="element-3772" data-mp="2eq4m7ippg5sp">
          {rows.map(label => <li key={label} className="relative" data-id="element-3773" data-mp="2erfthsiczvok">
              <span className="absolute -left-4 top-1/2 w-3 h-px bg-[#4F46E5]/60" data-id="element-3774" data-mp="29rur183zd573" />
              <div className={`${floatCard} px-3 py-2.5 flex items-center gap-2.5`} data-id="element-3775" data-mp="24z1l4gwrzqtf">
                <CheckCircle2Icon className="w-4 h-4 shrink-0 text-[#4F46E5]" strokeWidth={1.8} data-id="element-3776" data-mp="2018gvtf196vs" />
                <span className="font-sans text-[11.5px] text-[#121212]" data-id="element-3777" data-mp="1v25ao5nbgum8">{label}</span>
              </div>
            </li>)}
        </ul>
      </div>
    </div>;
}
export function ShiftVisual({
  icon,
  isFR
}: {
  icon: ShiftIconId;
  isFR: boolean;
}) {
  if (icon === 'sparkles') return <ActionVisual isFR={isFR} data-id="element-3778" data-mp="1h1umjade5u4r" />;
  if (icon === 'identity') return <ContextVisual isFR={isFR} data-id="element-3779" data-mp="1h3p6alcxfg79" />;
  return <TrustVisual isFR={isFR} data-id="element-3780" data-mp="3geqa1dtlazwv" />;
}